/**
 * Held-bet manual-approval alarm tunables (AB#8783, extracted from
 * layout/components/navbar/navbar.ts).
 *
 * These are operator-experience settings: how long the alarm sounds, how fast
 * it pulses, and its timbre. They shape how urgent/annoying the alert feels to
 * a trader working the manual-approval queue.
 *
 * PO/UX-OWNED — CALIBRATE BEFORE RETUNING. The values below are the current
 * as-shipped behaviour, not a designed target. Any change to how the alarm
 * sounds is a reviewed data change (PO/UX sign-off), not a code tweak buried in
 * the audio routine. Extraction (AB#8783) preserves behaviour exactly; the
 * calibration of what these SHOULD be is a later, separate change.
 */

/**
 * Total time (seconds) the held-bet alarm sounds before auto-stopping.
 * Bounds both the Web Audio pulse-train scheduling and the safety
 * `setTimeout` that tears the alert down. Longer = more insistent.
 */
export const MANUAL_APPROVAL_ALERT_DURATION_SECONDS = 60;

/**
 * Upper bound (milliseconds) the alarm waits for a suspended AudioContext to
 * resume before giving up on THAT arrival (AB#9470). This is a TECHNICAL
 * reliability guard, not a PO/UX tunable: `AudioContext.resume()` normally
 * settles in a few milliseconds, but some browsers can leave the promise
 * pending indefinitely when audio output is unavailable. Racing the resume
 * against this timeout keeps a failed resume from hanging the alert path and
 * lets the skip be logged (AC5) and retried on the next user interaction,
 * instead of the pre-fix synchronous re-check that dropped the first
 * post-suspend alert silently.
 */
export const ALERT_RESUME_TIMEOUT_MS = 1000;

/**
 * Gap (seconds) between the START of consecutive alarm pulses — the alarm's
 * cadence/tempo. Smaller = faster, more urgent-sounding pulsing.
 */
export const ALERT_PULSE_INTERVAL_SECONDS = 0.65;

/**
 * Audible length (seconds) of each individual pulse. Must stay shorter than
 * {@link ALERT_PULSE_INTERVAL_SECONDS} so pulses are discrete beeps with a gap
 * between them rather than a continuous tone.
 */
export const ALERT_PULSE_DURATION_SECONDS = 0.22;

/**
 * Time (seconds) from a pulse's start to its gain peak — the attack. A short
 * attack gives the pulse a sharp, alerting transient.
 */
export const ALERT_PULSE_ATTACK_SECONDS = 0.015;

/**
 * Two alternating tone frequencies (Hz) the alarm cycles between pulse-to-pulse
 * (even pulse index -> first, odd -> second). The alternation is what makes the
 * alarm read as a two-tone siren rather than a single repeated beep.
 */
export const ALERT_TONE_HZ: readonly [number, number] = [1244, 1661];

/**
 * Harmonic multiplier for the secondary oscillator relative to the primary
 * tone (a perfect fifth). Adds body/harshness so the alert cuts through room
 * noise.
 */
export const ALERT_HARMONIC_RATIO = 1.5;

/**
 * Detune (cents) applied to the secondary oscillator. A small detune thickens
 * the tone (mild chorus/beating) so the alarm sounds less pure/synthetic.
 */
export const ALERT_DETUNE_CENTS = 22;

/**
 * Peak gain (0–1) at the top of each pulse's attack ramp — the alarm's
 * loudness. Raise to make the alert more prominent; keep below 1 to avoid
 * clipping.
 */
export const ALERT_GAIN_PEAK = 0.28;

/**
 * Near-silent gain floor used as the ramp start/end target. Web Audio's
 * `exponentialRampToValueAtTime` cannot ramp to exactly 0, so this tiny
 * positive value stands in for silence between pulses.
 */
export const ALERT_GAIN_FLOOR = 0.0001;
